import Link from 'next/link';
import { useRouter } from 'next/router';

import { NextPage } from 'next';

import { getUrlWithHost } from '@/helpers/url';
import { useActiveTheme } from '@/hooks/useActiveTheme';
import { useCapabilities } from '@/hooks/useCapabilities';
import useCompanyCustomizations from '@/hooks/useCompanyCustomizations';
import { useIsStorefrontChannel } from '@/hooks/useIsStorefrontChannel';
import { useTranslation } from '@/i18n';
import { CustomTab } from '@/models/customizationModels';
import { selectAccountCustomTabs, selectStorefrontCustomization } from '@/redux/customizations/reducers';
import { useAppSelector } from '@/redux/hooks';
import { envVars } from '@/services/ApiService';

const pick = (tri: 'show' | 'hide' | 'inherit' | undefined, fallback: boolean) =>
  tri === 'show' ? true : tri === 'hide' ? false : fallback;

const ProfileDropDown: NextPage = () => {
  const tabs = useAppSelector(selectAccountCustomTabs);
  const companyCustomizations = useCompanyCustomizations();
  const storefrontCustomizations = useAppSelector(selectStorefrontCustomization);
  const isStorefront = useIsStorefrontChannel();
  const { theme } = useActiveTheme();

  const caps = useCapabilities();
  const router = useRouter();
  const { t } = useTranslation('profile');

  const onClick = (link: string) => router.push(getUrlWithHost(link, envVars.checkHost));

  const onCustomTabClick = (tab: CustomTab) => {
    if (tab.source) return void window.open(tab.source, '_blank');
    onClick(`/${tab.slug}`);
  };

  const baseShowProfile = companyCustomizations.tabs.show_profile;
  const baseShowLogout = isStorefront ? (storefrontCustomizations?.tabs.show_logout ?? true) : companyCustomizations.tabs.show_logout;
  const hasCustomTabs = !!tabs?.length;

  const showProfile = pick(caps.profileMenu?.profile, baseShowProfile);
  const showLogout = baseShowLogout; // Always show logout if enabled in customizations
  const showCustomTabs = pick(caps.profileMenu?.customTabs, hasCustomTabs) && hasCustomTabs;

  const optionsCount = (showProfile ? 1 : 0) + (showLogout ? 1 : 0) + (showCustomTabs ? tabs!.length : 0);
  if (optionsCount === 0) return null;

  return (
    <div className="dropdown ProfileDropDown">
      <button
        aria-expanded="false"
        className="btn btn-secondary dropdown-toggle d-flex align-items-center p-1"
        data-bs-toggle="dropdown"
        id="profileDropDown"
        type="button"
      >
        <span className="icon-profile c-white f-16 m-auto" />
        <p>{t('myAccount')}</p>
        <i className="icon-arrow-down ms-auto me-2 mt-1 f-10" />
      </button>

      <ul aria-labelledby="profileDropDown" className={`dropdown-menu ${theme === 'dark' && 'dropdown-menu-dark'}`}>
        {showProfile && (
          <li>
            <a className="dropdown-item" onClick={() => onClick('/app/account')}>
              {t('myProfile')}
            </a>
          </li>
        )}

        {showCustomTabs &&
          tabs!.map((tab) => (
            <li key={tab.uid}>
              <a className="dropdown-item" onClick={() => onCustomTabClick(tab)}>
                {tab.name}
              </a>
            </li>
          ))}

        {showLogout && (
          <li>
            <Link className="dropdown-item" href={getUrlWithHost('/logout', true)} scroll={false}>
              {t('logout')}
            </Link>
          </li>
        )}
      </ul>
    </div>
  );
};

export default ProfileDropDown;
